import { LoadingOutlined } from "@ant-design/icons";
import { Modal, message } from "antd";
import {
  DashboardV1State,
  DeepLinkType,
  isExploreCard,
} from "pages/api/deep-links/types";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { useApplicationContext } from "src/hooks/useApplication";
import { SearchState } from "src/search/types";
import { cloneData } from "src/utils/objects";
import {
  DashboardRuntime,
  DashboardsPageContext,
  isDashboardId,
  isNewDashboardId,
  isStartScreenId,
  UNSAVED_ID,
  UNSAVED_TITLE,
} from "./context";
import { DashboardViewer } from "./DashboardViewer";
import { PrimaryQuery } from "./PrimaryQuery";
import { useRouter } from "next/router";
import {
  serializeParams,
  unserializeParams,
} from "src/search/lib/params-state-reducer";

export function DashboardsDomain(ctx: DashboardsPageContext) {
  const [props, prompt, editor] = useDashboardRuntime({
    ...ctx,
    source: "dashboard",
  });
  ctx.runtime.current = props;
  const { setTitle } = useApplicationContext();
  const title = props.name || "Empty board";
  useEffect(() => {
    if (title !== "Empty board") setTitle(`${title} (Board) - VZBL`);
  }, [title, setTitle]);
  return (
    <>
      {prompt}
      {editor}
      {(!props.dashboardId && props.readyToEdit) ||
      (props.dashboardId && isStartScreenId(props.dashboardId)) ? (
        <LoadingOutlined />
      ) : (
        <PrimaryQuery key={props.dashboardId} {...props}>
          <DashboardViewer key={props.dashboardId} {...props} />
        </PrimaryQuery>
      )}
    </>
  );
}

export function useDashboardRuntime(
  props: DashboardsPageContext & {
    initialState?: DashboardV1State;
    source: "dashboard" | "embedded";
  }
): [
  DashboardsPageContext & DashboardRuntime,
  ReactElement,
  JSX.Element | null
] {
  const { dashboardId, openDashboard } = props;
  const {
    layout: initialLayout = [],
    data: initialData = {},
    ...initialExtra
  } = props.initialState || {};
  const [prompt, promptsContext] = Modal.useModal();
  const [editor, setEditor] = useState<JSX.Element | null>(null);
  const [layout, setLayout] =
    useState<DashboardV1State["layout"]>(initialLayout);
  const [data, setData] = useState<DashboardV1State["data"]>(initialData);
  const [extra, setExtra] =
    useState<Partial<Omit<DashboardV1State, "layout" | "data">>>(initialExtra);
  const [scope, setScope] = useState<
    undefined | "private" | "workspace" | "global"
  >(undefined);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reset, setReset] = useState<{
    layout: typeof layout;
    data: typeof data;
    extra: typeof extra;
    name: string;
    description: string;
    scope: typeof scope;
  }>({
    layout: initialLayout,
    data: initialData,
    extra: initialExtra,
    name: UNSAVED_TITLE,
    description: "",
    scope: undefined,
  });
  const [readyFor, setReadyFor] = useState("");
  const router = useRouter();
  const filter = router.query.filter || serializeParams({});
  const qsInitParams = unserializeParams(`${filter}`);
  const initDate = qsInitParams.duration;
  const initFilters = qsInitParams.filters;
  delete qsInitParams.duration;
  delete qsInitParams.filters;
  const [searchState, setSearchState] = useState<Partial<SearchState>>({
    duration: initDate || extra.defaultDateRange?.duration || "P7D",
    filters: initFilters || extra.defaultFilters || [],
    ...qsInitParams,
  });
  const boardLinks = useDeepLinks(DeepLinkType.BOARD);
  const board = (boardLinks.links.data || []).find(
    ({ id }) => id === dashboardId
  );
  if (
    !board &&
    boardLinks.links.isFetched &&
    (boardLinks.links.data?.length || 0) > 0
  ) {
    const byName = (boardLinks.links.data || []).find(
      ({ json }) =>
        dashboardId && (json as DashboardV1State).systemName === dashboardId
    );
    if (byName) {
      props.openDashboard(byName.id);
    }
  }
  const boards = useRef(boardLinks.links.data || []);
  boards.current = boardLinks.links.data || [];
  useEffect(() => {
    const link = boards.current.find(({ id }) => id === dashboardId);
    if (link?.json) {
      const { layout, data, ...extra } = link.json as DashboardV1State;
      const scope = link.userId
        ? "private"
        : link.orgId
        ? "workspace"
        : "global";
      setLayout(layout);
      setData(data);
      setExtra(extra);
      // TODO once we have more than a default date range, add that too
      setSearchState({
        duration: extra.defaultDateRange?.duration || "P1D",
        filters: extra.defaultFilters || [],
      });
      setName(link.name);
      setDescription(link.description || "");
      setScope(scope);
      setReset({
        layout,
        data,
        name: link.name,
        extra,
        scope,
        description: link.description || "",
      });
      setReadyFor(dashboardId!);
    } else if (isNewDashboardId(dashboardId)) {
      setData({});
      setLayout([]);
      setExtra({});
      setName(UNSAVED_TITLE);
      setDescription("");
      setScope("private");
      setReset({
        data: {},
        layout: [],
        name: UNSAVED_TITLE,
        description: "",
        extra: {},
        scope: "private",
      });
    } else {
      setReadyFor("");
    }
  }, [dashboardId]);
  const createRef = useRef(boardLinks.create.mutateAsync);
  createRef.current = boardLinks.create.mutateAsync;
  const updateRef = useRef(boardLinks.update.mutateAsync);
  updateRef.current = boardLinks.update.mutateAsync;
  const deleteRef = useRef(boardLinks.remove.mutateAsync);
  deleteRef.current = boardLinks.remove.mutateAsync;
  const serialized = JSON.stringify({ ...extra, layout, data, name, scope });
  const onSave = useCallback(
    async (
      values: Partial<
        {
          name: string;
          description: string;
          scope: "private" | "workspace";
        } & DashboardV1State
      > = {}
    ) => {
      const {
        layout: newLayout,
        data: newData,
        name: newName,
        description: newDescription,
        scope: newScope,
        ...newExtra
      } = { ...JSON.parse(serialized), ...values } as {
        name: string;
        description: string;
        scope?: "private" | "workspace";
      } & DashboardV1State;
      if (isNewDashboardId(dashboardId)) {
        return createRef
          .current({
            name: newName,
            description: newDescription,
            state: { layout: newLayout, data: newData, ...newExtra },
            scope: newScope || "private",
          })
          .then(({ id }) => {
            message.info(`Created "${newName}"`);
            openDashboard(id);
          });
      } else if (isDashboardId(dashboardId)) {
        setReset({
          name: newName,
          description: newDescription,
          layout: newLayout,
          data: newData,
          extra: newExtra,
          scope: newScope || "private",
        });
        return updateRef
          .current({
            id: dashboardId,
            name: newName,
            description: newDescription,
            state: { layout: newLayout, data: newData, ...newExtra },
            scope: newScope || "private",
          })
          .then(() => {
            message.info(`Updated "${newName}"`);
          });
      } else return undefined;
    },
    [serialized, dashboardId, openDashboard, setReset]
  );
  const onCancel = useCallback(() => {
    setLayout(reset.layout);
    setData(reset.data);
    setName(reset.name);
  }, [reset]);
  const onDelete = useCallback(() => {
    if (isDashboardId(dashboardId)) {
      prompt.confirm({ title: `Delete "${reset.name}"?` }).then(
        (answer) => {
          if (answer) {
            deleteRef.current({ id: dashboardId }).then(() => {
              message.info(`Deleted "${reset.name}"`);
              setName(UNSAVED_TITLE);
              setDescription("");
              openDashboard(UNSAVED_ID);
            });
          }
          Modal.destroyAll();
        },
        (...args) => {
          Modal.destroyAll();
          message.warning("Unexpected error.");
          console.debug("modal error", ...args);
        }
      );
    }
  }, [dashboardId, reset, prompt, openDashboard]);
  const onRemoveCard = useCallback(
    (id: string) => {
      setLayout((state) => state.filter(({ i }) => i !== id));
      setData((state) => {
        const copy = {
          ...state,
        };
        delete copy[id];
        return copy;
      });
    },
    [setLayout, setData]
  );
  const mutationInProcess =
    boardLinks.create.isLoading ||
    boardLinks.update.isLoading ||
    boardLinks.remove.isLoading;

  const { isSuperAdmin, isOrgAdmin } = useApplicationContext();
  const userCanCreate = isOrgAdmin;
  const userCanEdit = Boolean(
    board?.userId || isSuperAdmin || (board?.orgId && isOrgAdmin)
  );

  return [
    {
      ...props,
      extra,
      layout,
      data,
      description,
      name,
      scope,
      setDescription,
      setExtra,
      setName,
      setData,
      setLayout,
      setScope,
      searchState,
      setSearchState,
      mutationInProcess,
      onSave,
      onCancel,
      onDelete,
      onRemoveCard,
      setEditor,
      userCanEdit,
      userCanCreate,
      readyToEdit: readyFor === dashboardId,
    },
    promptsContext,
    editor,
  ];
}

export function mergeStateIntoDashboard(
  dashboardJson: DashboardV1State,
  state: Partial<SearchState>
) {
  const cards = Object.entries(dashboardJson.data);
  cards.forEach(([key, card]) => {
    if (isExploreCard(card)) {
      const merged = cloneData(card.state);
      merged.params.orgId = state.orgId || merged.params.orgId;
      if (Array.isArray(state.select) && state.select.length > 0) {
        merged.params.select = state.select;
      }
      if (Array.isArray(state.groupBys) && state.groupBys.length > 0) {
        merged.params.groupBys = state.groupBys;
      }
      if (state.aggregations?.length) {
        merged.params.aggregations = state.aggregations;
      }
      if (Array.isArray(state.filters) && state.filters.length > 0) {
        let newFilters = cloneData(merged.params.filters || []);
        state.filters.forEach((filter) => {
          newFilters = newFilters.filter(
            (filter_) => filter_.key !== filter.key
          );
          newFilters.push(filter);
          merged.params.filters = newFilters;
        });
      }
      if (state.compareWith) {
        merged.params.compareWith = state.compareWith;
      }
      (dashboardJson.data[key] as typeof card).state = merged;
    }
  });
  dashboardJson.defaultDateRange = {
    duration: state.duration || undefined,
    startDate: state.startDate,
    endDate: state.endDate,
  };
  return dashboardJson;
}
